import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm text-sm font-medium ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        // Luxury variants for Bait Al-Teeb
        bronze: "bg-gradient-to-r from-amber-700 via-amber-600 to-yellow-600 text-amber-50 shadow-lg shadow-amber-900/30 hover:shadow-amber-600/40 hover:-translate-y-0.5 font-semibold tracking-wide",
        gold: "bg-gradient-to-r from-yellow-600 via-amber-500 to-amber-600 text-amber-950 shadow-lg shadow-yellow-700/30 hover:shadow-yellow-500/40 hover:-translate-y-0.5 font-semibold tracking-wide",
        luxuryOutline: "border-2 border-primary bg-transparent text-primary hover:bg-primary/10 font-serif tracking-widest uppercase",
        elegant: "bg-muted text-foreground border border-border hover:border-primary hover:text-primary transition-colors",
      },
      size: {
        default: "h-10 px-6 py-2",
        sm: "h-9 rounded-sm px-4",
        lg: "h-12 rounded-sm px-10 text-base",
        xl: "h-14 rounded-sm px-12 text-lg",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
